import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import AlertText from 'fe-design-base/molecules/AlertText';
import { List } from 'immutable';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import {
  getAssistedClockInEnabled,
  getAssistedClockOutEnabled,
} from 'selectors/session';

import { ASSISTED_STATUSES } from 'features/timesheets/constants';
import AssistedClockOutIndicator from 'features/timesheets/TimesheetsPage/dailyReview/DailyReviewView/TableRow/AssistedClockOutIndicator';
import {
  TIME_FORMAT,
  TIME_FORMAT_MOBILE,
} from 'features/timesheets/TimesheetsPage/TimecardModal/constants';
import Notes from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/ManagerNoteSection/Notes';
import Issue from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/TimecardSection/Issues/issue';
import AssistedClockInNote from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/AssistedClockInNote';
import AssistedClockOutNote from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/AssistedClockOutNote';
import {
  ISSUE_COMPONENT_MAP,
  ISSUE_PRIORITIES,
} from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/Issues/constants';
import Section from 'features/timesheets/TimesheetsPage/TimecardModal/Section';
import {
  formattedScheduledTime,
  getTimecardDuration,
} from 'features/timesheets/TimesheetsPage/TimecardModal/utils';
import {
  TIMECARD_SELECT_INPUTS,
  trackDropDownDeleteClicked,
  trackDropDownInputSelected,
  trackDropDownMenuClick,
} from 'features/timesheets/tracking';
import { overnightShiftDateAdjustment } from 'features/timesheets/util';

import TimePicker from 'components/forms/TimePicker';

import { cxHelpers } from 'util/className';
import { moment, momentInZone } from 'util/dateTime';
import { toI18n } from 'util/i18n';

const { cx, cxEl } = cxHelpers('TimecardSection');

const DROPDOWN_TRACKING_PROP = {
  actual_start_at: TIMECARD_SELECT_INPUTS.CLOCK_IN,
  actual_end_at: TIMECARD_SELECT_INPUTS.CLOCK_OUT,
};

const WORKED_NOTE_EVENT_TYPES = Object.freeze([
  'clock_in',
  'clock_out',
  'missed_clock_in',
  'missed_clock_out',
  'missed_shift',
]);

const { REJECTED, INITIATED } = ASSISTED_STATUSES;

const getPriority = type => ISSUE_PRIORITIES[type] ?? ISSUE_PRIORITIES.default;

const TimecardSection = ({
  startAt,
  endAt,
  onChangeShift,
  dateStr,
  validationErrors,
  scheduled,
  scheduledStartAt,
  scheduledEndAt,
  employeeName,
  shift,
  setChangesMade,
  focusClockOut = false,
}) => {
  const clockOutTimePickerRef = useRef(null);
  const mobileClockOutInputRef = useRef(null);
  const assistedClockInEnabled = useSelector(getAssistedClockInEnabled);
  const assistedClockOutEnabled = useSelector(getAssistedClockOutEnabled);
  const assistedClockIn = shift.get('assisted_clock_in');
  const assistedClockOut = shift.get('assisted_clock_out');
  const workedNotes = (shift.get('notes') || List()).filter(note =>
    WORKED_NOTE_EVENT_TYPES.includes(note.get('event_type'))
  );

  useEffect(() => {
    if (startAt > endAt) {
      onChangeShift(
        'actual_end_at',
        overnightShiftDateAdjustment(startAt, endAt)
      );
    }
  }, [startAt, endAt, onChangeShift]);

  const currentIssues = useMemo(() => {
    const issues = shift.get('issues');
    if (!issues) return null;

    const filteredIssues = issues.filter(
      issue => !issue.get('resolved') || issue.get('type') !== 'auto_clock_out'
    );

    return filteredIssues.sort((a, b) => {
      const aType = a.get('type');
      const bType = b.get('type');
      return getPriority(aType) - getPriority(bType);
    });
  }, [shift]);

  useEffect(() => {
    if (focusClockOut) {
      if (isMobile && mobileClockOutInputRef.current) {
        mobileClockOutInputRef.current.focus();
      } else if (!isMobile && clockOutTimePickerRef.current) {
        const inputElement =
          clockOutTimePickerRef.current.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }
    }
  }, [focusClockOut]);

  const handleTimeChange = useCallback(
    ({ target: { name, value } }) => {
      onChangeShift(name, value && `${dateStr} ${value}`);
      setChangesMade(true);
    },
    [dateStr, onChangeShift, setChangesMade]
  );

  const handleClear = useCallback(
    name => {
      trackDropDownDeleteClicked({
        properties: { drop_down: DROPDOWN_TRACKING_PROP[name] },
      });
      setChangesMade(true);
    },
    [setChangesMade]
  );

  const handleSelect = useCallback(name => {
    trackDropDownInputSelected({
      properties: { drop_down: DROPDOWN_TRACKING_PROP[name] },
    });
  }, []);

  const handleMobileTimeChange = useCallback(
    (name, e) => {
      const value = e.target.value;
      onChangeShift(name, value && `${dateStr} ${value}`);
      setChangesMade(true);
    },
    [onChangeShift, dateStr, setChangesMade]
  );

  const timecardDuration = useMemo(
    () => getTimecardDuration(startAt, endAt),
    [startAt, endAt]
  );

  const timecardStartAt = useMemo(
    () => (scheduled ? moment(scheduledStartAt) : momentInZone()),
    [scheduled, scheduledStartAt]
  );

  const timecardEndAt = useMemo(
    () => (scheduled ? moment(scheduledEndAt) : momentInZone()),
    [scheduled, scheduledEndAt]
  );

  const handleOpen = useCallback(timeType => {
    trackDropDownMenuClick({
      properties: { drop_down: DROPDOWN_TRACKING_PROP[timeType] },
    });
  }, []);

  const momentScheduledStartAt = useMemo(
    () => moment(scheduledStartAt),
    [scheduledStartAt]
  );
  const momentScheduledEndAt = useMemo(
    () => moment(scheduledEndAt),
    [scheduledEndAt]
  );

  return (
    <Box className={cx()}>
      <Section
        bottomBorder
        bottomPadding
        collapsedContent={
          <Box column>
            {(shift.get('feedback')?.get('body') ||
              shift.get('feedback')?.get('rating')) && (
              <Notes
                employeeName={employeeName}
                feedback={shift.get('feedback')}
                type="feedback"
              />
            )}
            {workedNotes.count() > 0 && (
              <Notes notes={workedNotes} type="worked_notes" />
            )}
            <Box row vcenter w={296}>
              <Box vcenter className={cxEl('range')}>
                {isMobile ? (
                  <Box>
                    <Box row vcenter spaceevenly>
                      <Box w={82} className={cxEl('start-at')}>
                        <Text
                          className="clockedIn"
                          color="mono700"
                          textAlign="left"
                          variant="bodySmBoldTight"
                        >
                          {toI18n(
                            'timecard_modal_v3.timecard_section.clocked_in'
                          )}
                        </Text>
                      </Box>
                      <Box w={82} className={cxEl('end-at')}>
                        <Text
                          className="clockedOut"
                          color="mono700"
                          textAlign="left"
                          variant="bodySmBoldTight"
                        >
                          {toI18n(
                            'timecard_modal_v3.timecard_section.clocked_out'
                          )}
                        </Text>
                      </Box>
                    </Box>
                    <Box row vcenter spaceevenly>
                      <Box w={82} className={cxEl('start-at')}>
                        <input
                          type="time"
                          name="actual_start_at"
                          style={{
                            fontSize: '14px',
                            padding: '3px',
                            lineHeight: '40px',
                          }}
                          value={startAt && startAt.format(TIME_FORMAT_MOBILE)}
                          onChange={partial(
                            handleMobileTimeChange,
                            'actual_start_at'
                          )}
                          autoComplete="false"
                        />
                      </Box>
                      <Box mh={8}>-</Box>
                      <Box w={82} className={cxEl('end-at')}>
                        <input
                          ref={mobileClockOutInputRef}
                          type="time"
                          name="actual_end_at"
                          style={{
                            fontSize: '14px',
                            padding: '3px',
                            lineHeight: '40px',
                          }}
                          value={endAt && endAt.format(TIME_FORMAT_MOBILE)}
                          onChange={partial(
                            handleMobileTimeChange,
                            'actual_end_at'
                          )}
                          autoComplete="false"
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box row vcenter>
                    <Box className={cxEl('start-at')}>
                      <Box mb={8}>
                        <Text
                          className="Clocked In"
                          color="mono700"
                          textAlign="left"
                          variant="bodySmBold"
                        >
                          {toI18n(
                            'timecard_modal_v3.timecard_edit_section.clocked_in'
                          )}
                        </Text>
                      </Box>
                      <TimePicker
                        name="actual_start_at"
                        dataTestId="time-card-start-time"
                        validationError={validationErrors.length > 0}
                        value={startAt ? startAt.format(TIME_FORMAT) : null}
                        onChange={handleTimeChange}
                        onOpen={partial(handleOpen, 'actual_start_at')}
                        onClear={partial(handleClear, 'actual_start_at')}
                        onSelect={partial(handleSelect, 'actual_start_at')}
                        overrideOnFocus={!startAt}
                        timeToOverride={timecardStartAt.format(TIME_FORMAT)}
                        iconSize="xsmall"
                        endIconName="Close"
                      />
                    </Box>
                    <Box mh={4} mt={28}>
                      -
                    </Box>
                    <Box className={cxEl('end-at')}>
                      <Box mb={8}>
                        <Text
                          className="Clocked Out"
                          color="mono700"
                          textAlign="left"
                          variant="bodySmBold"
                        >
                          {toI18n(
                            'timecard_modal_v3.timecard_edit_section.clocked_out'
                          )}
                        </Text>
                      </Box>
                      <TimePicker
                        ref={clockOutTimePickerRef}
                        name="actual_end_at"
                        dataTestId="time-card-end-time"
                        validationError={validationErrors.length > 0}
                        value={endAt ? endAt.format(TIME_FORMAT) : null}
                        onChange={handleTimeChange}
                        onClear={partial(handleClear, 'actual_end_at')}
                        onSelect={partial(handleSelect, 'actual_end_at')}
                        onOpen={partial(handleOpen, 'actual_end_at')}
                        overrideOnFocus={!!startAt && !endAt}
                        timeToOverride={timecardEndAt.format(TIME_FORMAT)}
                        iconSize="xsmall"
                        endIconName="Close"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            {validationErrors.length > 0 && (
              <Box mt={8} className={cxEl('validation-error')}>
                <AlertText variant="error" message={validationErrors} />
              </Box>
            )}
            {scheduled && (
              <Box mt={16} mb={0}>
                <Text
                  variant="captions"
                  color="mono700"
                  i18n="timecard_modal_v3.timecard_section.scheduled"
                  i18nProps={{
                    time: formattedScheduledTime(
                      scheduled,
                      momentScheduledStartAt,
                      momentScheduledEndAt
                    ),
                  }}
                />
              </Box>
            )}
            {(currentIssues || [])
              .filter(issue =>
                Object.keys(ISSUE_COMPONENT_MAP).includes(issue.get('type'))
              )
              .map((issue, key) => (
                <Box mt={10} key={key}>
                  <Issue issue={issue} />
                </Box>
              ))}
            {assistedClockInEnabled &&
              assistedClockIn &&
              assistedClockIn.get('status') !== REJECTED &&
              assistedClockIn.get('comment') && (
                <Box mt={8}>
                  <AssistedClockInNote
                    submittedAt={assistedClockIn.get('submitted_at')}
                    comment={assistedClockIn.get('comment')}
                  />
                </Box>
              )}
            {assistedClockOutEnabled &&
              assistedClockOut &&
              assistedClockOut.get('status') !== REJECTED &&
              assistedClockOut.get('comment') && (
                <Box mt={8}>
                  <AssistedClockOutNote
                    submittedAt={assistedClockOut.get('submitted_at')}
                    comment={assistedClockOut.get('comment')}
                  />
                </Box>
              )}
            {assistedClockOutEnabled &&
              assistedClockOut &&
              assistedClockOut.get('status') !== REJECTED &&
              assistedClockOut.get('status') === INITIATED && (
                <Box mt={8}>
                  <AssistedClockOutIndicator
                    status={assistedClockOut.get('status')}
                    pastDue={assistedClockOut.get('past_due')}
                  />
                </Box>
              )}
          </Box>
        }
        iconName="TimeClock"
        title={toI18n('timecard_modal_v3.timecard_edit_section.worked', {
          props: { time: timecardDuration },
        })}
      />
    </Box>
  );
};

TimecardSection.propTypes = {
  startAt: PropTypes.object,
  endAt: PropTypes.object,
  dateStr: PropTypes.string,
  onChangeShift: PropTypes.func.isRequired,
  validationErrors: PropTypes.array,
  scheduled: PropTypes.bool,
  scheduledStartAt: PropTypes.string,
  scheduledEndAt: PropTypes.string,
  shift: ImmutablePropTypes.map.isRequired,
  employeeName: PropTypes.string,
  setChangesMade: PropTypes.func.isRequired,
  focusClockOut: PropTypes.bool,
};

export default TimecardSection;
